/**
 * The API client.
 *
 * Every call carries the ID token; a 401 means the session ran out rather than that
 * something is wrong, so it is surfaced as a distinct error the app answers by signing in
 * again instead of showing a red box.
 */

import { idToken } from "./auth";
import { config } from "./config";
import type {
  Framework,
  FrameworkInput,
  FrameworksPage,
  Assessment,
  AssessmentMode,
  AssessmentPreview,
  DocumentsPage,
  Action,
  ActionPatch,
  ActionsPage,
  BoardPaperPreview,
  Client,
  HandbookSearch,
  Item,
  ItemPatch,
  ItemPreview,
  NewItemFields,
  PageUpdate,
  Page,
  RegisterFirm,
  Source,
  SourceActivity,
  SubscriptionEvent,
} from "./types";

export class SessionExpired extends Error {
  constructor() {
    super("Your session has expired.");
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  if (!token) throw new SessionExpired();

  const response = await fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401) throw new SessionExpired();

  const text = await response.text();
  const body = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new ApiError(body.message ?? `Request failed (${response.status})`, response.status, body.detail);
  }
  return body as T;
}

export interface ItemQuery {
  status?: string;
  month?: string;
  regulator?: string;
  client?: string;
  category?: string;
  source?: string;
  limit?: number;
  cursor?: string;
}

function queryString(query: ItemQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const rendered = params.toString();
  return rendered ? `?${rendered}` : "";
}

export const api = {
  listItems: (query: ItemQuery = {}) => request<Page<Item>>(`/items${queryString(query)}`),

  /**
   * Everything with a next step still to do, worst first. Unpaginated: the server sorts
   * it, and a page boundary in the middle of a worst-first list hides the tail.
   */
  listActions: (includeComplete = false) =>
    request<ActionsPage>(`/actions${includeComplete ? "?include_complete=true" : ""}`),

  /**
   * Every page of a query, following the cursor.
   *
   * Necessary rather than tidy: DynamoDB applies filters *after* reading a page, so a
   * filtered query can return an empty page and still have more results behind it. Stopping
   * at the first short page would silently hide items from her log.
   */
  async listAllItems(query: ItemQuery = {}, pageLimit = 20): Promise<Item[]> {
    const items: Item[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < pageLimit; page++) {
      const result = await request<Page<Item>>(
        `/items${queryString({ ...query, limit: 200, cursor })}`,
      );
      items.push(...result.items);
      cursor = result.cursor;
      if (!cursor) break;
    }
    return items;
  },

  getItem: (id: string) => request<Item>(`/items/${id}`),

  /**
   * Pages that changed after she filed them, and the ones she has muted.
   *
   * Both halves, from one call: a suppression she cannot see is one she cannot undo, and
   * the row she set it on is by then hidden.
   */
  listChangedItems: () =>
    request<{ items: Item[]; count: number; muted: Item[]; muted_count: number }>(
      "/items/changed",
    ),

  /**
   * Her decision on a revised page: update the log, update it with an action, or stop
   * tracking. The API reads the change from the item and refuses (409) if it is no longer the
   * one `changed_on` names, so a tab left open cannot decide on a revision it never showed.
   */
  applyPageUpdate: (id: string, update: PageUpdate) =>
    request<Item>(`/items/${id}/page-update`, { method: "POST", body: JSON.stringify(update) }),

  patchItem: (id: string, patch: ItemPatch) =>
    request<Item>(`/items/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  /**
   * One item's actions, for the Next steps popup in the log.
   *
   * Fetched when she opens the popup rather than carried on every row of the grid: an item has
   * nought to three of these, the log pages a few hundred rows at a time, and the cell already
   * shows the derived summary of them without needing the records themselves.
   */
  listItemActions: (itemId: string) =>
    request<{ items: Action[] }>(`/items/${itemId}/actions`),

  createAction: (itemId: string, fields: ActionPatch) =>
    request<Action>(`/items/${itemId}/actions`, {
      method: "POST",
      body: JSON.stringify(fields),
    }),

  patchAction: (itemId: string, actionId: string, patch: ActionPatch) =>
    request<Action>(`/items/${itemId}/actions/${actionId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteAction: (itemId: string, actionId: string) =>
    request<{ deleted: string }>(`/items/${itemId}/actions/${actionId}`, { method: "DELETE" }),

  bulkPatch: (ids: string[], patch: ItemPatch) =>
    request<{ updated: Item[]; failed: { id: string; error: string }[]; count: number }>(
      "/items/bulk",
      { method: "POST", body: JSON.stringify({ ids, patch }) },
    ),

  /**
   * Apply one patch to any number of items.
   *
   * The endpoint caps a single call at 100 ids, and "select all" over a 220-item backlog
   * sails past that. Chunking here rather than in the view means the cap is one place, and
   * she never sees a 400 for doing exactly what the button offered.
   */
  bulkPatchAll: async (ids: string[], patch: ItemPatch) => {
    const CHUNK = 100;
    let count = 0;
    const failed: { id: string; error: string }[] = [];
    for (let at = 0; at < ids.length; at += CHUNK) {
      const result = await api.bulkPatch(ids.slice(at, at + CHUNK), patch);
      count += result.count;
      failed.push(...result.failed);
    }
    return { count, failed };
  },

  /**
   * What this link would become, without creating anything.
   *
   * Reads the page for its title and publication date and asks the model for a summary, so
   * the Add dialog is filled in from the article rather than from today's date. Costs one
   * model call — unless the URL is already in the log, which it answers for nothing.
   */
  previewItem: (url: string) =>
    request<ItemPreview>(`/items/preview?url=${encodeURIComponent(url)}`),

  addItem: (url: string, fields: NewItemFields = {}) =>
    request<Item>("/items", { method: "POST", body: JSON.stringify({ url, ...fields }) }),

  listClients: () => request<Page<Client>>("/clients"),

  /** Her tag vocabulary. The same file constrains what the model may suggest. */
  listTags: () => request<Page<string>>("/tags"),

  addClient: (fields: Partial<Client>) =>
    request<Client>("/clients", { method: "POST", body: JSON.stringify(fields) }),

  getClient: (id: string) => request<Client>(`/clients/${id}`),

  /** Live FS Register lookup. Nothing is stored, so every call is a fresh read. */
  clientRegister: (id: string) => request<RegisterFirm>(`/clients/${id}/register`),

  patchClient: (id: string, fields: Partial<Client>) =>
    request<Client>(`/clients/${id}`, { method: "PATCH", body: JSON.stringify(fields) }),

  listSources: () => request<Page<Source>>("/sources"),

  sourceActivity: (days = 7) => request<SourceActivity>(`/sources/activity?days=${days}`),

  setSourceEnabled: (id: string, enabled: boolean) =>
    request<Source>(`/sources/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),

  releaseHeld: (id: string, since?: string) =>
    request<{ source_id: string; released: number; remaining: number }>(
      `/sources/${id}/release`,
      { method: "POST", body: JSON.stringify(since ? { since } : {}) },
    ),

  listSubscriptions: (approval: "pending" | "approved" | "rejected" = "pending") =>
    request<Page<SubscriptionEvent>>(`/subscriptions?approval=${approval}`),

  decideSubscription: (id: string, approval: "approved" | "rejected", note = "") =>
    request<SubscriptionEvent>(`/subscriptions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ approval, note }),
    }),

  /**
   * Find a rule. `q` is a keyword search over Handbook chapter and section names; `ref` is an
   * exact reference lookup for the click-a-reference path, which returns one hit or none.
   */
  handbookSearch: (query: { q?: string; ref?: string }) =>
    request<HandbookSearch>(`/handbook/search${queryString(query as ItemQuery)}`),

  /** What a board paper would say. No S3, no model call — free to click. */
  previewBoardPaper: (client: string, month: string) =>
    request<BoardPaperPreview>(
      `/board-papers/preview?client=${encodeURIComponent(client)}&month=${encodeURIComponent(month)}`,
    ),

  createBoardPaper: (client: string, month: string) =>
    request<{ url: string; filename: string; expires_in: number; item_count?: number }>(
      "/board-papers",
      { method: "POST", body: JSON.stringify({ client, month }) },
    ),

  createExport: (filters: { status?: string; month?: string; client?: string }) =>
    request<{ url: string; filename: string; count: number; expires_in: number }>("/exports", {
      method: "POST",
      body: JSON.stringify(filters),
    }),

  /**
   * What an impact assessment would work from, and what stands in its way. A GET that reads the
   * database and nothing else — no model call, no fetch of the publication — so it is free and
   * she can look before every run.
   */
  previewAssessment: (item: string) =>
    request<AssessmentPreview>(
      `/assessments/preview?item=${encodeURIComponent(item)}`,
    ),

  /**
   * Ask for one. Returns immediately at `queued` — generating takes minutes, so the work happens
   * in a worker and this only accepts it. Re-running the same pairing replaces its draft.
   */
  createAssessment: (item: string, reading?: { mode: AssessmentMode; sections?: number[] }) =>
    request<Assessment>("/assessments", {
      method: "POST",
      body: JSON.stringify({ item, ...(reading ?? {}) }),
    }),

  /**
   * The In progress list: runs still going, and runs that produced nothing and have not been
   * cleared. Finished documents are not here — they are with every other generated file.
   */
  activeAssessments: () =>
    request<{ items: Assessment[]; count: number }>("/assessments?view=active"),

  /** Clear a failed or refused run from In progress. Nothing is deleted. */
  dismissAssessment: (item: string) =>
    request<Assessment>("/assessments/dismiss", {
      method: "POST",
      body: JSON.stringify({ item }),
    }),

  /** List the sections of a run that stopped before it could. A fetch, no model call, free. */
  outlineAssessment: (item: string) =>
    request<Assessment>("/assessments/outline", {
      method: "POST",
      body: JSON.stringify({ item }),
    }),

  /** One publication's assessment, for polling. */
  getAssessment: (item: string) =>
    request<Assessment>(`/assessments?item=${encodeURIComponent(item)}`),

  /** Recent runs, newest first, each with a fresh download link if there is a draft behind it. */
  listAssessments: () => request<{ items: Assessment[]; count: number }>("/assessments"),

  /**
   * Everything the system has ever produced, newest first, each with a fresh link.
   *
   * Built from the bucket rather than from any record: Excel exports and board papers write
   * the file and nothing else, so a list assembled from job records would show only
   * assessments while looking like the complete set.
   */
  /**
   * The regulatory frameworks a client's proportionality basis can name, and the spellings a
   * publication might use for each. Hers to maintain — see `SourcesView`.
   */
  listFrameworks: () => request<FrameworksPage>("/frameworks"),

  createFramework: (fields: FrameworkInput) =>
    request<Framework>("/frameworks", { method: "POST", body: JSON.stringify(fields) }),

  patchFramework: (id: string, fields: Partial<FrameworkInput>) =>
    request<Framework>(`/frameworks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  deleteFramework: (id: string) =>
    request<{ deleted: string }>(`/frameworks/${id}`, { method: "DELETE" }),

  listDocuments: () => request<DocumentsPage>("/documents"),

  /**
   * Remove one generated document from the bucket, for good.
   *
   * The only destructive call in this client. `ref` is what the listing gave back — the key
   * with the export prefix taken off — so nothing outside `exports/` can be named from here.
   * Nothing is versioned: there is no undo behind this.
   */
  deleteDocument: (ref: string) =>
    request<{ deleted: string; filename: string; kind: string }>(
      `/documents?ref=${encodeURIComponent(ref)}`,
      { method: "DELETE" },
    ),
};
